import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Map, { Layer, Marker, NavigationControl, Source } from "react-map-gl";
import type { FeatureCollection } from "geojson";
import {
    AlertTriangle,
    Building2,
    CalendarClock,
    Gauge,
    MapPin,
    Pause,
    Pencil,
    Play,
    RefreshCw,
    ScrollText,
    Square,
    Users,
    Zap,
} from "lucide-react";
import "mapbox-gl/dist/mapbox-gl.css";
import BONavigation from "../../../components/admin/BONavigation";
import {
    estimateZoneRun,
    getActiveZoneRun,
    getSavedZoneSelection,
    getZonePrices,
    getZoneReport,
    getZoneRunLogs,
    getZoneServices,
    saveZoneSelection,
    setZonePrice,
    startZoneRun,
    stopZoneRun,
    ZoneEnrichmentBreakdown,
    ZoneLogEntry,
    ZonePrice,
    ZoneReport,
    ZoneRun,
    ZoneServiceCatalogItem,
    ZoneServicesCatalog,
    ZoneEstimate,
} from "../../../interfaces/zoneAdmin";

const MAPBOX_TOKEN =
    process.env.REACT_APP_MAPBOX_TOKEN ||
    "pk.eyJ1IjoibWF0aGlldWR1Y2tmbG93IiwiYSI6ImNtajc5cnQ1aDAxeGEzcHNhZWtub3I5N3QifQ.BaEcgEbT0TKEYgeBX0KwVw";

/** Paris — a starting view, not a default selection. Nothing is queried until a pin is set. */
const INITIAL_VIEW = { longitude: 2.3522, latitude: 48.8566, zoom: 10 };

// ─── Formatting ────────────────────────────────────────────────────────────────

const fmt = (n: number | null | undefined): string =>
    n === null || n === undefined ? "—" : n.toLocaleString("fr-FR");

const fmtUsd = (n: number | null | undefined): string => {
    if (n === null || n === undefined) return "—";
    const digits = n !== 0 && Math.abs(n) < 0.01 ? 4 : 2;
    return `${n.toLocaleString("fr-FR", { minimumFractionDigits: digits, maximumFractionDigits: digits })} $`;
};

const fmtDuration = (hours: number | null): string => {
    if (hours === null || !Number.isFinite(hours)) return "—";
    if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
    if (hours < 48) return `${hours.toFixed(1)} h`;
    return `${(hours / 24).toFixed(1)} j`;
};

/** Enrichment statuses, ordered as the pipeline walks them rather than alphabetically. */
const STATUS_ORDER = [
    "pending",
    "processing",
    "waiting_callback",
    "retry",
    "done",
    "approved",
    "rejected",
    "error",
    "closed",
];

const STATUS_STYLES: Record<string, string> = {
    pending: "bg-gray-100 text-gray-600",
    processing: "bg-blue-100 text-blue-700",
    waiting_callback: "bg-indigo-100 text-indigo-700",
    retry: "bg-amber-100 text-amber-700",
    done: "bg-teal-100 text-teal-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-rose-100 text-rose-700",
    error: "bg-red-100 text-red-700",
    closed: "bg-gray-200 text-gray-500",
};

function orderStatuses(byStatus: Record<string, number>): [string, number][] {
    const known = STATUS_ORDER.filter((s) => byStatus[s] !== undefined).map(
        (s) => [s, byStatus[s]] as [string, number]
    );
    const extra = Object.keys(byStatus)
        .filter((s) => !STATUS_ORDER.includes(s))
        .map((s) => [s, byStatus[s]] as [string, number]);
    return [...known, ...extra];
}

/**
 * The selection circle, as a polygon.
 *
 * Degrees per kilometre are taken at the pin's own latitude, so the shape stays a circle on
 * the ground instead of stretching as the map moves north — the same correction the query
 * behind it applies with $centerSphere.
 */
function circlePolygon(center: [number, number], radiusKm: number, steps = 96): FeatureCollection {
    const [lng, lat] = center;
    const kmPerDegLat = 110.574;
    const kmPerDegLng = 111.32 * Math.cos((lat * Math.PI) / 180);
    const ring: [number, number][] = [];
    for (let i = 0; i <= steps; i++) {
        const theta = (i / steps) * 2 * Math.PI;
        ring.push([
            lng + (radiusKm / kmPerDegLng) * Math.cos(theta),
            lat + (radiusKm / kmPerDegLat) * Math.sin(theta),
        ]);
    }
    return {
        type: "FeatureCollection",
        features: [{ type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [ring] } }],
    };
}

// ─── Small pieces ──────────────────────────────────────────────────────────────

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
    return (
        <div className="border border-gray-200 rounded-lg bg-white px-4 py-3">
            <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
            <p className="text-xl font-semibold text-gray-900 mt-0.5">{value}</p>
            {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
        </div>
    );
}

function BreakdownTable({
    title,
    icon: Icon,
    total,
    totalLabel,
    rows,
}: {
    title: string;
    icon: any;
    total: number;
    totalLabel: string;
    rows: ZoneEnrichmentBreakdown[];
}) {
    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Icon className="w-4 h-4 text-gray-400" />
                    {title}
                </p>
                <p className="text-xs text-gray-500">
                    {fmt(total)} {totalLabel}
                </p>
            </div>
            {rows.length === 0 ? (
                <p className="px-4 py-3 text-xs text-gray-400">Aucun enrichissement dans cette zone.</p>
            ) : (
                <div className="divide-y divide-gray-100">
                    {rows.map((row) => (
                        <div key={row.type} className="px-4 py-2.5">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className="text-xs font-medium text-gray-800 font-mono">{row.type}</span>
                                <span className="text-xs text-gray-500">{fmt(row.total)}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {orderStatuses(row.byStatus).map(([status, count]) => (
                                    <span
                                        key={status}
                                        className={`text-[11px] px-1.5 py-0.5 rounded ${
                                            STATUS_STYLES[status] ?? "bg-gray-100 text-gray-600"
                                        }`}
                                    >
                                        {status} {fmt(count)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ActiveRunBanner({
    run,
    onStop,
    stopping,
}: {
    run: ZoneRun;
    onStop: () => void;
    stopping: boolean;
}) {
    const budgetPct =
        run.budgetUsd && run.budgetUsd > 0 ? Math.min(100, (run.spentUsd / run.budgetUsd) * 100) : null;

    return (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-4 mb-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Accélération en cours — {run.label}
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                        {fmt(run.cursor)} / {fmt(run.targetCount)} cibles distribuées ·{" "}
                        {fmtUsd(run.spentUsd)} dépensés
                        {run.budgetUsd !== null && ` sur ${fmtUsd(run.budgetUsd)} de plafond`}
                    </p>
                    <p className="text-[11px] text-amber-700 mt-0.5">
                        Restauration automatique le{" "}
                        {new Date(run.focusUntil).toLocaleString("fr-FR")}
                    </p>
                    {run.pausedServices.length > 0 && (
                        <p className="text-[11px] text-amber-700 mt-1">
                            En pause : {run.pausedServices.map((p) => p.key).join(", ")}
                        </p>
                    )}
                    {run.enabledService && (
                        <p className="text-[11px] text-amber-700 mt-1">
                            {run.enabledService.key} a été activé pour ce run — il sera remis hors service à
                            la fin.
                        </p>
                    )}
                </div>
                <button
                    onClick={onStop}
                    disabled={stopping}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-amber-900 text-white hover:bg-amber-800 disabled:opacity-50"
                >
                    <Square className="w-3.5 h-3.5" />
                    {stopping ? "Arrêt…" : "Arrêter et tout rétablir"}
                </button>
            </div>

            <div className="mt-3 h-1.5 bg-amber-200 rounded overflow-hidden">
                <div className="h-full bg-amber-600" style={{ width: `${run.progressPct}%` }} />
            </div>
            {budgetPct !== null && budgetPct > 80 && (
                <p className="text-[11px] text-amber-900 mt-1.5 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {budgetPct.toFixed(0)} % du plafond consommé — le run s'arrêtera de lui-même en l'atteignant.
                </p>
            )}
        </div>
    );
}

// ─── Live logs ─────────────────────────────────────────────────────────────────

const LOG_LEVEL_STYLES: Record<string, string> = {
    info: "text-gray-600",
    warn: "text-amber-700",
    error: "text-red-600",
};

/**
 * The run's activity trail, polled while it is live.
 *
 * The run executes on the enrichment worker, not on the process serving this page, so there is
 * nothing to stream from here — the lines are read from the database the worker writes them to.
 * Each poll asks only for what is newer than the last line already held.
 */
function RunLogPanel({ runId, live }: { runId: string; live: boolean }) {
    const [entries, setEntries] = useState<ZoneLogEntry[]>([]);
    const [autoScroll, setAutoScroll] = useState(true);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const lastAtRef = useRef<string | undefined>(undefined);

    // A new run starts a new trail; without this the previous run's lines would linger and the
    // `since` cursor would suppress the new run's early lines.
    useEffect(() => {
        setEntries([]);
        lastAtRef.current = undefined;
    }, [runId]);

    useEffect(() => {
        let cancelled = false;

        const poll = async () => {
            try {
                const fresh = await getZoneRunLogs(runId, lastAtRef.current);
                if (cancelled || fresh.length === 0) return;
                lastAtRef.current = fresh[fresh.length - 1].at;
                // Capped: a long run would otherwise grow this list without bound.
                setEntries((prev) => [...prev, ...fresh].slice(-500));
            } catch {
                /* transient — the next tick retries */
            }
        };

        poll();
        // Finished runs get one read; only a live run needs to keep asking.
        if (!live) return () => { cancelled = true; };
        const id = setInterval(poll, 4_000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [runId, live]);

    useEffect(() => {
        if (autoScroll) bottomRef.current?.scrollIntoView({ block: "nearest" });
    }, [entries, autoScroll]);

    return (
        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden mb-4">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <ScrollText className="w-4 h-4 text-gray-400" />
                    Journal du run
                    {live && (
                        <span className="flex items-center gap-1 text-[11px] text-green-600 font-normal">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> en direct
                        </span>
                    )}
                </p>
                <label className="flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={autoScroll}
                        onChange={(e) => setAutoScroll(e.target.checked)}
                        className="accent-gray-900"
                    />
                    défilement auto
                </label>
            </div>
            <div className="max-h-64 overflow-y-auto px-4 py-2 font-mono text-[11px] leading-relaxed">
                {entries.length === 0 ? (
                    <p className="text-gray-400 py-2">
                        Aucune ligne pour l'instant. Les cibles sont distribuées au rythme du worker —
                        la première ligne d'activité peut prendre quelques minutes.
                    </p>
                ) : (
                    entries.map((entry) => (
                        <div key={entry._id} className="flex gap-2">
                            <span className="text-gray-300 shrink-0">
                                {new Date(entry.at).toLocaleTimeString("fr-FR")}
                            </span>
                            <span className={LOG_LEVEL_STYLES[entry.level] ?? "text-gray-600"}>
                                {entry.message}
                            </span>
                        </div>
                    ))
                )}
                <div ref={bottomRef} />
            </div>
        </div>
    );
}

// ─── Speed-up panel ────────────────────────────────────────────────────────────

function SpeedUpPanel({
    service,
    eligibleInZone,
    enabled,
    selection,
    disabled,
    defaultTtlHours,
    maxTtlHours,
    onLaunched,
}: {
    service: ZoneServiceCatalogItem;
    eligibleInZone: number;
    enabled: boolean;
    selection: { center: [number, number]; radiusKm: number };
    disabled: boolean;
    defaultTtlHours: number;
    maxTtlHours: number;
    onLaunched: () => void;
}) {
    const [pct, setPct] = useState(100);
    const [exactCount, setExactCount] = useState("");
    const [estimate, setEstimate] = useState<ZoneEstimate | null>(null);
    const [estimating, setEstimating] = useState(false);
    const [budget, setBudget] = useState("");
    const [ttlHours, setTtlHours] = useState(defaultTtlHours);
    const [launching, setLaunching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Every estimate walks the zone server-side. A debounce keeps dragging the slider from
    // queueing one of those per pixel.
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const limit = exactCount.trim() === "" ? undefined : Number(exactCount);

    useEffect(() => {
        if (eligibleInZone === 0) {
            setEstimate(null);
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setEstimating(true);
            try {
                const next = await estimateZoneRun(selection, service.key, {
                    selectedPct: pct,
                    limit: limit && limit > 0 ? limit : undefined,
                });
                setEstimate(next);
                // Prefill the ceiling with the estimate — the number the operator just read is
                // the one they are agreeing to, so it should not need retyping.
                setBudget((current) =>
                    current === "" && next.estimatedUsd !== null ? next.estimatedUsd.toFixed(2) : current
                );
            } catch {
                setEstimate(null);
            } finally {
                setEstimating(false);
            }
        }, 500);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pct, exactCount, eligibleInZone, service.key, selection.center[0], selection.center[1], selection.radiusKm]);

    const launch = async () => {
        setLaunching(true);
        setError(null);
        try {
            await startZoneRun(selection, service.key, {
                selectedPct: pct,
                limit: limit && limit > 0 ? limit : undefined,
                budgetUsd: budget.trim() === "" ? null : Number(budget),
                ttlHours,
            });
            onLaunched();
        } catch (err: any) {
            setError(err?.response?.data?.error ?? err.message ?? "Échec du lancement");
        } finally {
            setLaunching(false);
        }
    };

    const selectedCount = estimate?.selectedCount ?? 0;

    return (
        <div className="border border-gray-200 rounded-lg bg-white p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{service.label}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                        {service.targetModel === "Person" ? "Personnes" : "Events"} · {service.pacingNote}
                    </p>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-lg font-semibold text-gray-900">{fmt(eligibleInZone)}</p>
                    <p className="text-[11px] text-gray-400">à traiter ici</p>
                </div>
            </div>

            {!enabled && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-3">
                    Ce service est désactivé sur /bo/services. L'accélération l'activera le temps du run,
                    puis le remettra hors service.
                </p>
            )}

            {eligibleInZone === 0 ? (
                <p className="text-xs text-gray-400">Rien d'éligible dans cette zone.</p>
            ) : (
                <>
                    <div className="flex items-center gap-3 mb-2">
                        <input
                            type="range"
                            min={1}
                            max={100}
                            value={pct}
                            disabled={!!limit}
                            onChange={(e) => setPct(Number(e.target.value))}
                            className="flex-1 accent-gray-900 disabled:opacity-40"
                        />
                        <span className="text-xs text-gray-600 w-10 text-right">{pct} %</span>
                    </div>

                    <div className="flex items-center gap-2 mb-3">
                        <label className="text-[11px] text-gray-400">ou exactement</label>
                        <input
                            type="number"
                            min={1}
                            max={eligibleInZone}
                            value={exactCount}
                            placeholder="ex. 560"
                            onChange={(e) => setExactCount(e.target.value)}
                            className="border border-gray-200 rounded px-2 py-0.5 text-xs w-24"
                        />
                        {limit ? (
                            <button
                                onClick={() => setExactCount("")}
                                className="text-[11px] text-gray-400 hover:text-gray-700 underline"
                            >
                                revenir au %
                            </button>
                        ) : null}
                    </div>

                    <div className="grid grid-cols-3 gap-2 border-y border-gray-100 py-2.5 mb-3">
                        <div>
                            <p className="text-[11px] text-gray-400">Sélection</p>
                            <p className="text-sm font-semibold text-gray-900">{fmt(selectedCount)}</p>
                        </div>
                        <div>
                            <p className="text-[11px] text-gray-400">Coût estimé</p>
                            <p className="text-sm font-semibold text-gray-900">
                                {service.apiService === null ? "gratuit" : fmtUsd(estimate?.estimatedUsd)}
                            </p>
                        </div>
                        <div>
                            <p className="text-[11px] text-gray-400">Durée estimée</p>
                            <p className="text-sm font-semibold text-gray-900">
                                {fmtDuration(estimate?.estimatedHours ?? null)}
                            </p>
                        </div>
                    </div>

                    {estimate && (
                        <p className="text-[11px] text-gray-400 mb-3">
                            {estimate.calls > 0
                                ? `${fmt(estimate.calls)} appels (${estimate.callsPerItem}/doc)`
                                : "Aucun appel facturé"}
                            {estimate.costPerCall !== null && ` · ${fmtUsd(estimate.costPerCall)}/appel`}
                            {service.apiService !== null && estimate.costPerCall === null && (
                                <span className="text-amber-700"> · prix non renseigné pour {service.apiService}</span>
                            )}
                            {" · débit "}
                            {estimate.rateSource === "observed" ? "mesuré" : "nominal"} :{" "}
                            {fmt(Math.round(estimate.itemsPerHour))}/h
                        </p>
                    )}

                    <div className="flex items-end gap-2 flex-wrap">
                        <div>
                            <label className="text-[11px] text-gray-400 block mb-0.5">Plafond ($)</label>
                            <input
                                type="number"
                                min={0}
                                step="0.01"
                                value={budget}
                                onChange={(e) => setBudget(e.target.value)}
                                disabled={service.apiService === null}
                                className="border border-gray-200 rounded px-2 py-1 text-xs w-24 disabled:bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="text-[11px] text-gray-400 block mb-0.5">Rétablir après (h)</label>
                            <input
                                type="number"
                                min={1}
                                max={maxTtlHours}
                                value={ttlHours}
                                onChange={(e) => setTtlHours(Number(e.target.value))}
                                className="border border-gray-200 rounded px-2 py-1 text-xs w-20"
                            />
                        </div>
                        <button
                            onClick={launch}
                            disabled={disabled || launching || estimating || selectedCount === 0}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40"
                            title={disabled ? "Une accélération est déjà en cours" : undefined}
                        >
                            <Play className="w-3.5 h-3.5" />
                            {launching ? "Lancement…" : "Accélérer"}
                        </button>
                    </div>

                    {service.pausesOnFocus.length > 0 && (
                        <p className="text-[11px] text-gray-400 mt-2">
                            Mettra en pause : {service.pausesOnFocus.join(", ")} — l'API et le reste de
                            l'application continuent de tourner.
                        </p>
                    )}
                    {service.pausesOnFocus.length === 0 && (
                        <p className="text-[11px] text-gray-400 mt-2">
                            Aucun service ne partage ce quota : rien à mettre en pause, l'accélération ne sert
                            qu'à cibler la zone.
                        </p>
                    )}
                    {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
                </>
            )}
        </div>
    );
}

// ─── Price editor ──────────────────────────────────────────────────────────────

function PriceEditor({ prices, onSaved }: { prices: ZonePrice[]; onSaved: (next: ZonePrice[]) => void }) {
    const [editing, setEditing] = useState<string | null>(null);
    const [value, setValue] = useState("");
    const [saving, setSaving] = useState(false);

    const save = async (serviceName: string) => {
        setSaving(true);
        try {
            onSaved(await setZonePrice(serviceName, value.trim() === "" ? null : Number(value)));
            setEditing(null);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="border border-gray-200 rounded-lg bg-white p-4">
            <p className="text-sm font-semibold text-gray-900 mb-1">Prix unitaires</p>
            <p className="text-[11px] text-gray-400 mb-3">
                Prix d'un appel facturé, utilisé par toutes les estimations. Vider le champ rétablit le
                tarif par défaut du code.
            </p>
            <div className="divide-y divide-gray-100">
                {prices.map((price) => (
                    <div key={price.serviceName} className="flex items-center justify-between py-2">
                        <div>
                            <p className="text-xs font-mono text-gray-700">{price.serviceName}</p>
                            <p className="text-[11px] text-gray-400">
                                {price.isCustom ? "personnalisé" : "valeur par défaut"} · {price.keyCount} clé(s)
                            </p>
                        </div>
                        {editing === price.serviceName ? (
                            <div className="flex items-center gap-1">
                                <input
                                    type="number"
                                    step="0.0001"
                                    min={0}
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    className="border border-gray-200 rounded px-1.5 py-0.5 text-xs w-24"
                                />
                                <button
                                    onClick={() => save(price.serviceName)}
                                    disabled={saving}
                                    className="text-xs px-2 py-0.5 rounded bg-gray-900 text-white disabled:opacity-40"
                                >
                                    OK
                                </button>
                                <button
                                    onClick={() => setEditing(null)}
                                    className="text-xs px-1.5 py-0.5 text-gray-400 hover:text-gray-700"
                                >
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => {
                                    setEditing(price.serviceName);
                                    setValue(price.costPerCall === null ? "" : String(price.costPerCall));
                                }}
                                className="flex items-center gap-1.5 text-xs text-gray-700 hover:text-gray-900"
                            >
                                {price.costPerCall === null ? (
                                    <span className="text-amber-700">non renseigné</span>
                                ) : (
                                    <span>{fmtUsd(price.costPerCall)}</span>
                                )}
                                <Pencil className="w-3 h-3 text-gray-400" />
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function ZoneControl() {
    const [catalog, setCatalog] = useState<ZoneServicesCatalog | null>(null);
    const [prices, setPrices] = useState<ZonePrice[]>([]);
    const [pin, setPin] = useState<[number, number] | null>(null);
    const [diameterKm, setDiameterKm] = useState(20);
    const [report, setReport] = useState<ZoneReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeRun, setActiveRun] = useState<ZoneRun | null>(null);
    const [stopping, setStopping] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null);
    /** Until the saved pin has been fetched, an empty map is "not loaded yet", not "no pin". */
    const [selectionLoaded, setSelectionLoaded] = useState(false);

    const radiusKm = diameterKm / 2;
    const selection = useMemo(
        () => (pin ? { center: pin, radiusKm } : null),
        [pin, radiusKm]
    );

    useEffect(() => {
        getZoneServices().then(setCatalog).catch(() => setError("Impossible de charger le catalogue"));
        getZonePrices().then(setPrices).catch(() => undefined);

        // The pin survives a reload because it lives in the database, not in this component.
        getSavedZoneSelection()
            .then((saved) => {
                if (saved) {
                    setPin(saved.center);
                    setDiameterKm(saved.radiusKm * 2);
                }
            })
            .catch(() => undefined)
            .finally(() => setSelectionLoaded(true));
    }, []);

    // Persist the selection as it changes, debounced — dragging the diameter slider would
    // otherwise write on every pixel. Skipped until the saved value has loaded, so the
    // default 20 km cannot overwrite what was stored before it arrives.
    useEffect(() => {
        if (!selectionLoaded || !selection) return;
        const id = setTimeout(() => {
            saveZoneSelection(selection).catch(() => undefined);
        }, 800);
        return () => clearTimeout(id);
    }, [selectionLoaded, selection]);

    const refreshRun = useCallback(async () => {
        try {
            setActiveRun(await getActiveZoneRun());
        } catch {
            /* transient */
        }
    }, []);

    // A run advances in the worker, not here, so the banner is polled rather than pushed.
    useEffect(() => {
        refreshRun();
        const id = setInterval(refreshRun, 10_000);
        return () => clearInterval(id);
    }, [refreshRun]);

    const analyse = async () => {
        if (!selection) return;
        setLoading(true);
        setError(null);
        try {
            setReport(await getZoneReport(selection, "full"));
            setLastRefreshAt(new Date());
        } catch (err: any) {
            setError(err?.response?.data?.error ?? err.message ?? "Analyse impossible");
        } finally {
            setLoading(false);
        }
    };

    const stop = async () => {
        if (!activeRun) return;
        setStopping(true);
        try {
            await stopZoneRun(activeRun._id);
            await refreshRun();
        } finally {
            setStopping(false);
        }
    };

    const circle = useMemo(
        () => (pin ? circlePolygon(pin, radiusKm) : null),
        [pin, radiusKm]
    );

    /** The report is only meaningful for the circle it was computed on. */
    const reportMatchesSelection =
        !!report &&
        !!selection &&
        report.center[0] === selection.center[0] &&
        report.center[1] === selection.center[1] &&
        report.radiusKm === selection.radiusKm;

    /**
     * Periodic refresh of the figures, every 30 s.
     *
     * Sends mode "counts", which re-counts the zone's cached id set rather than re-deriving
     * the zone — the geo pass and the event join are the expensive half and barely change
     * minute to minute, while the statuses are the half that actually moves during a run.
     * Only runs once a report exists for the current selection, so it can never fire the
     * heavy path behind the operator's back.
     */
    useEffect(() => {
        if (!autoRefresh || !selection || !reportMatchesSelection) return;

        const tick = async () => {
            // Don't stack a refresh on top of a full analysis still in flight.
            if (loading) return;
            setRefreshing(true);
            try {
                setReport(await getZoneReport(selection, "counts"));
                setLastRefreshAt(new Date());
            } catch {
                /* transient — the next tick retries */
            } finally {
                setRefreshing(false);
            }
        };

        const id = setInterval(tick, 30_000);
        return () => clearInterval(id);
    }, [autoRefresh, selection, reportMatchesSelection, loading]);

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-6 py-6">
                <BONavigation />

                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">Zone</h1>
                        <p className="text-xs text-gray-500 mt-0.5">
                            État d'enrichissement d'un secteur, et accélération ciblée dessus.
                        </p>
                    </div>
                    {reportMatchesSelection && (
                        <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-[11px] text-gray-400">
                                {refreshing
                                    ? "actualisation…"
                                    : lastRefreshAt
                                    ? `chiffres à jour à ${lastRefreshAt.toLocaleTimeString("fr-FR")}`
                                    : ""}
                            </span>
                            <button
                                onClick={() => setAutoRefresh((v) => !v)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border ${
                                    autoRefresh
                                        ? "border-gray-900 bg-gray-900 text-white"
                                        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                }`}
                                title="Recompte les statuts toutes les 30 s sans refaire le calcul géographique"
                            >
                                {autoRefresh ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                                Auto 30 s
                            </button>
                            <button
                                onClick={analyse}
                                disabled={loading}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                                Tout recalculer
                            </button>
                        </div>
                    )}
                </div>

                {activeRun && <ActiveRunBanner run={activeRun} onStop={stop} stopping={stopping} />}
                {activeRun && (
                    <RunLogPanel
                        runId={activeRun._id}
                        live={activeRun.status === "running" || activeRun.status === "pending"}
                    />
                )}

                {/* Map + selection */}
                <div className="border border-gray-200 rounded-lg bg-white overflow-hidden mb-4">
                    <div className="h-[380px] relative">
                        <Map
                            mapboxAccessToken={MAPBOX_TOKEN}
                            initialViewState={INITIAL_VIEW}
                            mapStyle="mapbox://styles/mapbox/light-v11"
                            onClick={(e) => setPin([e.lngLat.lng, e.lngLat.lat])}
                            cursor="crosshair"
                        >
                            <NavigationControl position="top-right" />
                            {circle && (
                                <Source id="zone-circle" type="geojson" data={circle}>
                                    <Layer
                                        id="zone-circle-fill"
                                        type="fill"
                                        paint={{ "fill-color": "#111827", "fill-opacity": 0.08 }}
                                    />
                                    <Layer
                                        id="zone-circle-line"
                                        type="line"
                                        paint={{ "line-color": "#111827", "line-width": 1.5 }}
                                    />
                                </Source>
                            )}
                            {pin && (
                                <Marker longitude={pin[0]} latitude={pin[1]} anchor="bottom">
                                    <MapPin className="w-7 h-7 text-gray-900" fill="#111827" />
                                </Marker>
                            )}
                        </Map>
                        {!pin && (
                            <div className="absolute inset-x-0 bottom-3 flex justify-center pointer-events-none">
                                <span className="bg-gray-900 text-white text-xs px-3 py-1.5 rounded-full shadow">
                                    Cliquez sur la carte pour poser le point
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-4 px-4 py-3 border-t border-gray-100 flex-wrap">
                        <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                            <label className="text-xs text-gray-500 whitespace-nowrap">Diamètre</label>
                            <input
                                type="range"
                                min={1}
                                max={catalog ? catalog.maxRadiusKm * 2 : 200}
                                value={diameterKm}
                                onChange={(e) => setDiameterKm(Number(e.target.value))}
                                className="flex-1 accent-gray-900"
                            />
                            <input
                                type="number"
                                min={1}
                                max={catalog ? catalog.maxRadiusKm * 2 : 200}
                                value={diameterKm}
                                onChange={(e) => setDiameterKm(Number(e.target.value))}
                                className="border border-gray-200 rounded px-2 py-1 text-xs w-20"
                            />
                            <span className="text-xs text-gray-400">km</span>
                        </div>
                        <button
                            onClick={analyse}
                            disabled={!pin || loading}
                            className="flex items-center gap-1.5 px-4 py-1.5 rounded text-xs font-medium bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40"
                        >
                            <Gauge className="w-3.5 h-3.5" />
                            {loading ? "Analyse…" : "Analyser la zone"}
                        </button>
                    </div>
                </div>

                {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                {loading && (
                    <p className="text-sm text-gray-500 mb-4">
                        Analyse en cours — un secteur dense peut prendre une trentaine de secondes.
                    </p>
                )}

                {report && !reportMatchesSelection && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
                        La sélection a changé depuis le dernier calcul. Relancez l'analyse pour des chiffres à
                        jour.
                    </p>
                )}

                {report && (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                            <Stat
                                label="Établissements"
                                value={fmt(report.establishments.total)}
                                hint={`rayon ${report.radiusKm.toFixed(1)} km`}
                            />
                            <Stat
                                label="Géolocalisés"
                                value={fmt(report.establishments.geocoded)}
                                hint="coordonnées propres"
                            />
                            <Stat
                                label="Via centroïde commune"
                                value={fmt(report.establishments.viaCentroid)}
                                hint="position approchée"
                            />
                            <Stat
                                label="Calcul"
                                value={`${(report.computeMs / 1000).toFixed(1)} s`}
                                hint={new Date(report.computedAt).toLocaleTimeString("fr-FR")}
                            />
                        </div>

                        {report.establishments.truncated && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4 flex items-start gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                Zone trop dense : le plafond de résolution a été atteint. Tous les chiffres
                                ci-dessous sont des minorants — réduisez le diamètre pour un compte exact.
                            </p>
                        )}
                        {report.establishments.viaCentroid > 0 && (
                            <p className="text-[11px] text-gray-500 mb-4">
                                {fmt(report.establishments.viaCentroid)} établissement(s) sans coordonnées
                                propres sont positionnés au centre de leur commune : près du bord du cercle,
                                leur appartenance à la zone est approximative.
                            </p>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
                            <BreakdownTable
                                title="Events"
                                icon={CalendarClock}
                                total={report.events.total}
                                totalLabel="events"
                                rows={report.events.enrichments}
                            />
                            <BreakdownTable
                                title="Personnes"
                                icon={Users}
                                total={report.people.total}
                                totalLabel="personnes"
                                rows={report.people.enrichments}
                            />
                        </div>

                        <div className="flex items-center gap-2 mb-3">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <p className="text-sm font-semibold text-gray-900">Accélérations</p>
                            {activeRun && (
                                <span className="text-[11px] text-amber-700">
                                    une accélération est déjà en cours — arrêtez-la pour en lancer une autre
                                </span>
                            )}
                        </div>

                        {catalog && selection && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
                                {catalog.services.map((service) => {
                                    const summary = report.services.find((s) => s.key === service.key);
                                    return (
                                        <SpeedUpPanel
                                            key={service.key}
                                            service={service}
                                            eligibleInZone={summary?.eligibleInZone ?? 0}
                                            enabled={summary?.enabled ?? false}
                                            selection={selection}
                                            disabled={!!activeRun || !reportMatchesSelection}
                                            defaultTtlHours={catalog.defaultTtlHours}
                                            maxTtlHours={catalog.maxTtlHours}
                                            onLaunched={refreshRun}
                                        />
                                    );
                                })}
                            </div>
                        )}

                        {prices.length > 0 && <PriceEditor prices={prices} onSaved={setPrices} />}
                    </>
                )}
            </div>
        </div>
    );
}
